import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum RemittanceAdviceLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  invoiceDate = 'invoiceDate',
  referenceNumber = 'referenceNumber',
  description = 'description',
  originalAmount = 'originalAmount', // original amount of the invoice - entered by user
  amountDue = 'amountDue', // amount less any existing payments
  discount = 'discount', // additional discount (absolute not percentage) applied to the invoice - entered by user
  payment = 'payment', // payment made against the invoice - entered by user
}

export const remittanceAdviceAddressSchema = yup.object().shape({
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})
export type remittanceAdviceAddress = yup.InferType<typeof remittanceAdviceAddressSchema>

export const remittanceAdviceFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: remittanceAdviceAddressSchema.nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  remittanceAdviceNumber: yup.string().nullable().optional(),
  paymentDate: yup.string().nullable().optional(),
  paymentReferenceNumber: yup.string().nullable().optional(),
  paymentType: yup.string().nullable().optional(),
  locale: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  paidTo: yup
    .object()
    .shape({
      companyName: yup.string().nullable().optional(),
      billingAddress: remittanceAdviceAddressSchema.nullable().optional(),
      attn: yup.string().nullable().optional(),
      phoneNumber: yup.string().nullable().optional(),
      emailAddress: yup.string().nullable().optional(),
    })
    .required(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        invoiceDate: yup.string().nullable().optional(),
        referenceNumber: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        originalAmount: yup.string().nullable().optional(),
        amountDue: yup.string().nullable().optional(),
        discount: yup.string().nullable().optional(),
        payment: yup.string().nullable().optional(),
      }),
    )
    .required(),
  notes: yup.string().nullable().optional(),
  footerText: yup.string().nullable().optional(),
})

export type RemittanceAdviceFileData = yup.InferType<typeof remittanceAdviceFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<RemittanceAdviceFileData>

export const remittanceAdviceFileMappings: RequiredFileData = {
  issuer: {
    companyName: '491dc1a8-3a11-4516-a51f-86b656aa4124',
    address: {
      addressLine1: '691de325-ca5b-4950-9676-c0dfe680519e',
      addressLine2: '654fa700-d5a2-4ffd-8b54-8dff3ebec134',
      postalCode: 'e42e480a-2744-464d-b22d-e204b71fd75b',
      city: 'e095487a-0360-40e2-bb71-b52d10d5ae54',
      state: 'f098f93a-3050-4c53-b4c4-982dd1fd4160',
      country: '199b2239-95bc-41e5-bb7a-148553158ef9',
    },
    phoneNumber: 'ec37f73b-b592-4ac1-9987-2d054fa5ba19',
    emailAddress: '0bb9c763-81d2-4371-87e5-52678689cb2b',
  },
  remittanceAdviceNumber: '98d1991f-4da3-4cad-9deb-5c4554c3765c',
  paymentDate: 'a9efb5d5-e21b-43b4-a8e1-e3dc750a250b',
  paymentReferenceNumber: '99dca802-5f18-4f7d-b5ce-005be1214162',
  paymentType: '39b9f962-6983-407c-b5a2-48324aa50f33',
  locale: 'a4a21488-59ef-42c1-9f14-dc1dc7972ee6',
  currency: '26daf9ee-66e3-428a-83e0-574273cf342c',
  paidTo: {
    companyName: 'bc3e888a-b5fc-486f-b7db-f22b1596d5f8',
    billingAddress: {
      addressLine1: '81162cc3-4334-4254-817b-4432655e4c35',
      addressLine2: '566fa999-f59f-47a1-a4e9-5873c6cb4e01',
      postalCode: '4e3231fe-b0d5-41a6-b688-b7edbd43c4af',
      city: 'ef6b95c2-6aad-42ef-bcf6-cd2ced5addbc',
      state: '0cd36d79-5bc2-40f2-a5d7-2653d4860ce7',
      country: '12fc2633-41db-4007-ac6c-0be7efc19e9f',
    },
    attn: 'a8ff22f8-622b-4b63-b7d6-833d286100fb',
    phoneNumber: 'e2043fa4-5645-430d-88a5-774ff5f51176',
    emailAddress: '5dd74a52-2180-4244-90bd-e39a0e23f259',
  },
  items: [
    {
      itemNumber: null, // Kept as null since it's computed
      invoiceDate: '38a9e2db-c605-4c03-9c2f-1a62c6ab9edf',
      referenceNumber: '28fe4f10-f441-4b6d-8268-d1cb6ef39e28',
      description: '6df451ee-45e2-40ba-ad1c-5114675f3ae9',
      originalAmount: 'ab5dbcba-0234-4ff7-978c-7bd656dfde3f',
      amountDue: '0a29308a-943c-452f-b322-1ba4bc91ab3f',
      discount: 'a048492b-9f87-469e-beda-965c869e4d12',
      payment: '898af315-f471-4ca0-b751-01f856e25dbe',
    },
  ],
  notes: '2c6cd64e-007d-42f8-bd75-e225cd4d78ce',
  footerText: '033424e6-3f1d-4671-a3b0-c768177ee6d9',
}
